import React, { Fragment } from "react";
import Page from "../../../components/page";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import { graphql } from "gatsby";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import {
  Heading1,
  Heading2,
  Body1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import CallToActionButton from "@vorne/gatsby-theme-marketing/components/button/calltoaction/callToAction";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import Routes from "../../../routes";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import BigIdea from "@vorne/gatsby-theme-marketing/components/bigidea/bigIdea";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import { Script } from "gatsby";

const ReduceDownTime = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>Reduce and Avoid Machine Downtime</PageTitle>
        <BigIdea>
          The first step to effectively reduce downtime in manufacturing is to
          establish consistent measurement standards and ensure downtime is
          captured at the process constraint. Then you can apply quick win
          strategies, such as providing real-time downtime data to operators,
          asking them to assign reasons to each down event, and attacking the
          largest sources of downtime.{" "}
        </BigIdea>
        <QuickLinks tag="h2" />
        <Heading1 id="whatIsMachineDowntime">
          What Is Machine Downtime?
        </Heading1>
        <Body1>
          Machine downtime is time accumulated when your manufacturing process
          is stopped for an unplanned event (e.g., a motor failure). While
          downtime is most often associated with equipment failures
          (breakdowns), it actually encompasses any unplanned event that causes
          your manufacturing process to stop. For example, downtime can be
          triggered by material issues, a shortage of operators, or unscheduled
          maintenance. The unifying element is that although production is
          scheduled, the process is not running due to an unplanned stop.
        </Body1>
        <Heading2>What Is a Planned Stop?</Heading2>
        <Body1>
          By way of contrast, a planned stop is any event where the process is
          unavailable to run due to a preplanned activity, such as a changeover
          or scheduled maintenance.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.downtimeOverTimeReportImage}
            alt="Line chart showing downtime over a period of time with a trend line."
            renderCaption={() => (
              <Fragment>
                Tracking Downtime (i.e. Unplanned Stop Time) with trend
                information provides a much clearer picture of the impact of
                focused improvement actions. In this example, the trend is
                excellent – because an improvement action taken on the labeler
                during the last few days was very effective and dramatically
                reduced Downtime.
              </Fragment>
            )}
          />
          <CallToActionButton url={Routes.Vorne.XLProductivityAppliance}>
            Reduce Your Downtime with Vorne
          </CallToActionButton>
        </Center>
        <Heading1 id="howToMeasureDowntime">How to Measure Downtime</Heading1>
        <Body1>
          In order to measure downtime accurately, it is important to create a
          clearly defined standard and then consistently apply that standard
          (over time and across equipment). It is particularly important to
          define the difference between downtime (an OEE Availability Loss) and
          small stops (an OEE Performance Loss). A good way to do this is to
          define a time-based threshold that differentiates between downtime and
          small stops, based on the idea that a reason should be captured for
          every downtime event, whereas it is not expected to capture a reason
          for every small stop (e.g. obstructed flow, jam, misfeed, sensor
          blocked, cleaning, checking). This time-based threshold can be based
          on absolute time or relative time.
        </Body1>
        <Heading2>How to Calculate Downtime</Heading2>
        <Body1>
          For downtime, we recommend the following simple definition based on
          absolute time:
        </Body1>
        <Body1>
          <i>
            Downtime events are unplanned stops that are long enough that we
            assign a reason for each occurrence. In practice, we define downtime
            as any unplanned stop that is five minutes or longer.
          </i>
        </Body1>
        <Body1>
          An alternative definition, based on relative time, integrates Ideal
          Cycle Time (the theoretical minimum time to produce one piece) into
          the definition. For example:
        </Body1>
        <Body1>
          <i>
            Downtime events are unplanned stops that last for more than 100
            manufacturing cycles (i.e., are 100 times longer than the Ideal
            Cycle Time).
          </i>
        </Body1>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.DowntimeCluster}>
            Learn More About Downtime
          </CallToActionButton>
        </Center>
        <Heading1 id="effectsOfDowntimeOnManufacturingProductivity">
          Effects of Downtime on Manufacturing Productivity
        </Heading1>
        <Body1>
          For most manufacturers, downtime is the single largest source of lost
          production time. It receives a high level of attention since equipment
          failures and breakdowns are highly visible (nothing is more
          frustrating than watching manufacturing equipment standing idle). As
          visible as downtime is, an amazing number of companies significantly
          underestimate their true downtime – especially when they estimate
          their downtime or manually record downtimes for each occurrence.
          Fortunately, this is an easy problem to fix (through simple automation
          as described later).
        </Body1>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.ImproveProductivity}>
            Learn How to Improve Your Manufacturing Productivity
          </CallToActionButton>
        </Center>
        <Body1>
          If your manufacturing process includes multiple pieces of equipment
          (such as in a packaging line) it is very important to focus attention
          on the constraint of the process. In particular, focus on downtime:
        </Body1>
        <List type="bullet">
          <ListItem>
            At the constraint itself (e.g., in a bottling line the constraint is
            often the filler)
          </ListItem>
          <ListItem>
            At upstream equipment that goes down and starves the constraint
          </ListItem>
          <ListItem>
            At downstream equipment that goes down and blocks the constraint
          </ListItem>
        </List>
        <Body1>
          Focusing improvement efforts on the constraint ensures optimal use of
          resources and is the most direct route to improved productivity and
          profitability.
        </Body1>
        <Body1>
          From the perspective of{" "}
          <Link url={Routes.Vorne.OverallEquipmentEffectiveness}>
            Overall Equipment Effectiveness (OEE)
          </Link>{" "}
          and Total Equipment Effectiveness (TEEP), downtime is captured as an
          Availability Loss. From the perspective of the{" "}
          <Link url={Routes.Vorne.SixBigLosses}>Six Big Losses</Link>, downtime
          is captured as an Unplanned Stop.
        </Body1>
        <Center $horizontally>
          <CallToActionButton
            className="wistia_embed wistia_async_8qz3bzlwio popover=true popoverAnimateThumbnail=true popoverContent=link"
            wistia
          >
            Watch a Video About One of the Deadly OEE Sins: Hiding Losses
          </CallToActionButton>
        </Center>

        <Heading1 id="bestPracticesForReducingDowntimeInManufacturing">
          Best Practices for Avoiding and Reducing Downtime in Manufacturing
        </Heading1>
        <Body1>
          The ultimate best practice for avoiding and reducing downtime is{" "}
          <Link url={Routes.Vorne.TPM}>Total Productive Maintenance (TPM)</Link>
          . TPM emphasizes proactive and preventative maintenance to maximize
          the operational efficiency of equipment. It blurs the distinction
          between the roles of production and maintenance by placing a strong
          emphasis on empowering operators to maintain their equipment. TPM has
          many elements: 5S, Autonomous Maintenance, Planned Maintenance,
          Quality Integration, Focused Improvement, New Equipment Management,
          Training, Education, Safety, and more. In fact, both OEE and the Six
          Big Losses are originally from the world of TPM.
        </Body1>
        <Body1>
          Implementing TPM is a substantial task that involves high levels of
          support from executive levels of an organization. If you don't
          currently have the resources to commit to a TPM program, here are four
          proven best practices that are very effective as quick wins for
          reducing downtime.
        </Body1>
        <Center $horizontally>
          <CallToActionButton url={Routes.Vorne.TPM}>
            Learn More About Total Productive Maintenance (TPM)
          </CallToActionButton>
        </Center>
        <Heading1 id="howToReduceMachineDowntime">
          How to Reduce Machine Downtime
        </Heading1>
        <Body1>
          Four highly effective strategies for reducing downtime are:
        </Body1>
        <List type="number">
          <ListItem>
            <Link url="#trackDownTimeAccurately">
              Automate Downtime Tracking for Accuracy
            </Link>
          </ListItem>
          <ListItem>
            <Link url="#categorizeDownTimeWithReasons">
              Categorize Downtime with Reasons
            </Link>
          </ListItem>
          <ListItem>
            <Link url="#exposeDownTimeInRealTime">
              Expose Downtime in Real Time
            </Link>
          </ListItem>
          <ListItem>
            <Link url="#attackTheLargestSourcesOfDownTime">
              Attack the Largest Sources of Downtime First
            </Link>
          </ListItem>
        </List>
        <Heading2 id="trackDownTimeAccurately">
          1. Automate Downtime Tracking for Accuracy
        </Heading2>
        <Body1>
          <strong>Problem:</strong> We are not sure if our downtime numbers are
          accurate. As a result, when we're reviewing our losses we don't know
          if we're working on the right things.
        </Body1>
        <Body1>
          <strong>Strategy:</strong> Replace manual tracking of downtime with
          automated tracking of downtime (track downtime based on equipment
          inputs rather than operator tick sheets).
        </Body1>
        <Body1>
          <strong>Solution:</strong> XL automatically detects downtime using a
          single input from your equipment (usually an existing sensor that
          counts parts or equipment cycles). XL records every downtime event
          with sub-second accuracy. The XL scoreboard shows downtime in
          real-time on the plant floor and XL provides downtime information to
          employees everywhere using patented technology and its integrated web
          server.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.lineDownScoreboardReportImage}
            alt="Downtime report with KPIs and a Pareto chart behind a scoreboard showing line down."
            renderCaption={() => (
              <Fragment>
                Whenever the line has an unscheduled stop, the XL scoreboard
                automatically switches to a downtime screen. It also logs every
                downtime event and provides browser-based reporting.
              </Fragment>
            )}
          />
          <CallToActionButton url={Routes.Vorne.MachineDowntimeTracking}>
            Learn More About Downtime Tracking
          </CallToActionButton>
        </Center>
        <Heading2 id="categorizeDownTimeWithReasons">
          2. Categorize Downtime with Reasons
        </Heading2>
        <Body1>
          <strong>Problem:</strong> We are having trouble prioritizing
          improvement actions because we don't have good enough information
          about why our equipment isn't running.
        </Body1>
        <Body1>
          <strong>Strategy:</strong> Capture reasons for each downtime event.
        </Body1>
        <Body1>
          <strong>Solution:</strong> It is essential to capture a reason for
          every downtime event to help you prioritize actions. XL makes this
          very easy – simply type in your downtime reasons and XL automatically
          generates a barcode sheet. Your operators scan barcodes to indicate
          reasons for each downtime event and XL automatically “snaps” reasons
          to the appropriate downtime event. We recommend starting simple –{" "}
          <Link url={Routes.Vorne.OptimizingDowntimeReasons}>
            {" "}
            no more than 25 reasons
          </Link>
          , one of which should be ‘All Other Losses’.
        </Body1>
        <Body1>
          As a best practice, make sure that every reason is clear (when looked
          at in the context of other reasons) and describes a symptom (as
          opposed to attempting to diagnose a root cause). Remove reasons that
          are rarely used and add reasons as needed to ensure that ‘All Other
          Losses’ is not in the top ten losses.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.realTimeProductionReportImage}
            alt="Downtime report with a total production timeline."
            renderCaption={() => (
              <Fragment>
                The Total Production Timeline™ visually shows downtime events
                (in red) and automatically groups them by reason over any
                selected time period (the Timeline Events table).
              </Fragment>
            )}
          />
          <CallToActionButton url={Routes.Vorne.OptimizingDowntimeReasons}>
            Learn How to Optimize Downtime Reasons
          </CallToActionButton>
        </Center>
        <Heading2 id="exposeDownTimeInRealTime">
          3. Expose Downtime in Real Time
        </Heading2>
        <Body1>
          <strong>Problem:</strong> We have multiple manufacturing lines, so
          it's hard for us to know where to focus our attention. We have reports
          about what happened in the past but we want to know where our biggest
          problems are right now – so we can fix them before they affect our
          numbers for this month.
        </Body1>
        <Body1>
          <strong>Strategy:</strong> Create a dashboard that shows you the
          instantaneous “right-now” status of your entire plant (including which
          lines are down).
        </Body1>
        <Body1>
          <strong>Solution:</strong> XL tracks the current state of every
          monitored production asset. Any combination of real-time metrics can
          be shown to give you whatever context you need for effective
          decision-making. Information can be viewed hierarchically (e.g., by
          department), and color is used to show the real-time “right now”
          status of each production asset.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.andonDowntimeReportImage}
            alt="Downtime report with eight andons, two of which are down."
            renderCaption={() => (
              <Fragment>
                All Production shows the real-time status of your equipment. In
                this example, it is instantly clear that Line 1 and Line 3 are
                down and also performing far worse than Line 2 and Line 4. Where
                should you focus your attention?
              </Fragment>
            )}
          />
          <CallToActionButton url="https://www.vorne.com/learn/downtime/machine-downtime-tracking-and-reporting/#downtimeReportingAndAnalysis">
            Learn About Downtime Dashboards
          </CallToActionButton>
        </Center>
        <Heading2 id="attackTheLargestSourcesOfDownTime">
          4. Attack the Largest Sources of Downtime First
        </Heading2>
        <Body1>
          <strong>Problem:</strong> Now that we are accurately tracking our
          downtime (with reasons), we want a simple but systematic approach to
          reducing downtime.
        </Body1>
        <Body1>
          <strong>Strategy:</strong> Use top loss information to systematically
          attack your largest sources of downtime.
        </Body1>
        <Body1>
          <strong>Solution:</strong> XL provides rich reporting and strong
          insights about where you are losing the most productivity. Use the Top
          Losses report to identify which downtime reason to attack next. Here's
          how:
        </Body1>
        <List type="bullet">
          <ListItem>
            Set the Date Range to <strong>Last 30 Days.</strong>
          </ListItem>
          <ListItem>Set the View to the appropriate shift.</ListItem>
          <ListItem>
            Identify one loss within the top five that the team feels confident
            they can improve.
          </ListItem>
          <ListItem>
            Agree on an improvement target and any external resources needed to
            reach that target.
          </ListItem>
        </List>
        <Body1>
          Post the report in the area. Review progress by monitoring Top Losses
          information.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.topLossesImage}
            alt="Top losses report with KPIs showing OEE, availability lost time, performance lost time, and quality lost time above a list of your top 5 losses ranked by lost time."
            renderCaption={() => (
              <Fragment>
                Top Losses packs in a lot of information and insights in a way
                that really focuses your employees on the most important losses
                to productivity.
              </Fragment>
            )}
          />
          <CallToActionButton url={Routes.Vorne.XL.FreeTrial}>
            Reduce Your Downtime with Vorne
          </CallToActionButton>
        </Center>
      </Section>
      <Script src="https://fast.wistia.com/embed/medias/sd70gqidus.jsonp" />
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" />
    </Page>
  );
};

export default ReduceDownTime;

export function Head() {
  return (
    <Seo
      title="Reduce and Avoid Machine Downtime"
      description="Learn four highly effective strategies and best practices for using real-time data to reduce machine downtime in manufacturing."
    />
  );
}
export const query = graphql`
  query {
    downtimeOverTimeReportImage: file(
      relativePath: { eq: "downtime-over-time-report-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    lineDownScoreboardReportImage: file(
      relativePath: { eq: "line-down-scoreboard-report-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    realTimeProductionReportImage: file(
      relativePath: { eq: "real-time-production-report-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    andonDowntimeReportImage: file(
      relativePath: { eq: "andon-downtime-report-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    topLossesImage: file(relativePath: { eq: "top-losses-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 600)
      }
    }
  }
`;
